import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  List,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Typography,
} from '@mui/material';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';

import { getLabelForEncodedFilterValue } from '../lib/columnFilters.js';

export interface ColumnFilterOption {
  value: string;
  label: string;
  count: number;
}

interface ColumnFilterMenuProps {
  label: string;
  options: ColumnFilterOption[];
  selectedValues: string[];
  onChange: (nextValues: string[]) => void;
}

const numberFormatter = new Intl.NumberFormat('cs-CZ');

const sanitizeValues = (values: string[]) => {
  const unique = Array.from(new Set(values));
  unique.sort((a, b) =>
    getLabelForEncodedFilterValue(a).localeCompare(getLabelForEncodedFilterValue(b), 'cs', {
      sensitivity: 'base',
    })
  );
  return unique;
};

export const ColumnFilterMenu = ({
  label,
  options,
  selectedValues,
  onChange,
}: ColumnFilterMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: string) => {
    const exists = selectedValues.includes(value);
    const nextValues = exists
      ? selectedValues.filter((item) => item !== value)
      : [...selectedValues, value];
    onChange(sanitizeValues(nextValues));
  };

  const handleSelectAll = () => {
    onChange(sanitizeValues(options.map((option) => option.value)));
  };

  const handleClear = () => {
    onChange([]);
  };

  const mergedOptions = useMemo(() => {
    const known = new Map(options.map((option) => [option.value, option]));
    for (const value of selectedValues) {
      if (!known.has(value)) {
        known.set(value, {
          value,
          label: getLabelForEncodedFilterValue(value),
          count: 0,
        });
      }
    }
    return Array.from(known.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'cs', { sensitivity: 'base' })
    );
  }, [options, selectedValues]);

  const hasSelection = selectedValues.length > 0;

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Filtrovat sloupec ${label}`}
        aria-pressed={hasSelection}
        onClick={handleOpen}
        color={hasSelection ? 'primary' : 'default'}
        sx={{
          color: hasSelection ? 'primary.main' : 'text.secondary',
          '&:hover': {
            color: hasSelection ? 'primary.dark' : 'text.primary',
          },
        }}
      >
        <FilterListIcon fontSize="inherit" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} keepMounted>
        <Box sx={{ px: 2, py: 1, width: 260 }}>
          <Stack spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              disabled={mergedOptions.length === 0}
            >
              Vybrat vše
            </Button>
            <Button size="small" onClick={handleClear} disabled={!hasSelection}>
              Vymazat
            </Button>
          </Stack>
        </Box>
        <Divider />
        {mergedOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Žádné dostupné hodnoty.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 320, overflowY: 'auto', minWidth: 260 }}>
            {mergedOptions.map((option) => {
              const checked = selectedValues.includes(option.value);
              return (
                <MenuItem key={option.value} onClick={() => handleToggle(option.value)}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      edge="start"
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={option.label}
                    secondary={`${numberFormatter.format(option.count)} položek`}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </MenuItem>
              );
            })}
          </List>
        )}
      </Menu>
    </>
  );
};
